import { shipmentService } from '@/services/ShipmentService';
import { ShipmentFields, ShipmentUpdateInput } from '@/types/graphql/schemaTypes';
import { operatorService } from '@/services/OperatorService';
import { ShipmentState, RootState } from '@/types/store';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import i18n from '@/plugins/i18n';

// initial state
const state: ShipmentState = {
  currentSearchCount: 0,
  newStatus: null,
  updateShipmentAttrs: {},
  currentShipment: null,
  action: ''
};

const getters: GetterTree<ShipmentState, RootState> = {
  shipmentCount(shipmentState: ShipmentState): number {
    return shipmentState.currentSearchCount;
  },
  currentShipment(shipmentState: ShipmentState): ShipmentFields | null {
    return shipmentState.currentShipment;
  }
};

// actions
const actions: ActionTree<ShipmentState, RootState> = {
  async setNewStatus({ commit }, params: string) {
    commit('setStatus', params);
  },
  async assign({ commit }, params: string) {
    operatorService.assignShipment(params).then(() => {
      // @TODO refresh shipment
    }).catch((error) => {
      commit('Alert/addAlert', { type: 'error', message: error.message }, { root: true });
    });
  },
  async fetchShipment({ commit }, params: string) {
    await shipmentService.fetchShipment(params).then((response) => {
      commit('setShipment', response.data!.shipments[0]);
    });
  },
  async updateShipment({ commit, dispatch }, shipmentUpdateArgs: ShipmentUpdateInput) {
    try {
      await shipmentService.updateShipment(shipmentUpdateArgs);
      dispatch('refreshShipment');
      commit('Alert/addAlert', {
        type: 'success',
        message: i18n.t('success.updated'),
        time: 5000
      }, { root: true });
    } catch (error) {
      commit('Alert/addAlert', { type: 'error', message: error.message }, { root: true });
      throw error;
    }
  },
  async refreshShipment({ state, dispatch, commit }) {
    commit('setAction', 'refresh');
    if (state.currentShipment) {
      dispatch('fetchShipment', state.currentShipment.id);
    }
  },
  async deleteShipment({ commit }, params: string) {
    shipmentService.deleteShipment(params).then((response) => {
      if ((response.data as any & { deleteShipment: any })!.deleteShipment) {
        commit('setAction', 'delete');
      }
    });
  },
  async unarchiveShipment({ commit }, params: string) {
    shipmentService.unarchiveShipment(params).then((response) => {
      if (response.data) {
        commit('setAction', 'delete');
      }
    });
  },
  async revertShipment({ dispatch, commit }, args: ShipmentUpdateInput) {
    dispatch('Inquiry/updateInquiry', args, { root: true }).then(() => {
      commit('setAction', 'revert');
    });
  },
  async setAction({ commit }, action: string) {
    commit('setAction', action);
  }
};

// mutations
const mutations: MutationTree<ShipmentState> = {
  updateShipmentAttrs(shipmentState, updateShipmentAttrs: ShipmentFields) {
    shipmentState.updateShipmentAttrs = { ...updateShipmentAttrs };
  },

  updateShipmentCount(shipmentState, newShipmentCount: number) {
    shipmentState.currentSearchCount = newShipmentCount;
  },

  setStatus(shipmentState, status: string) {
    shipmentState.newStatus = status;
  },

  clearNewStatus(shipmentState) {
    shipmentState.newStatus = null;
  },
  setShipment(shipmentState, shipment: ShipmentFields) {
    shipmentState.currentShipment = shipment;
  },
  setAction(shipmentState, action: string) {
    shipmentState.action = action;
  }
};

export const shipment: Module<ShipmentState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
